import {
  AlignmentType,
  BorderStyle,
  Document,
  Footer,
  HeadingLevel,
  Packer,
  PageNumber,
  Paragraph,
  TextRun,
} from 'docx';
import { saveAs } from 'file-saver';
import { PACK_DEFINITIONS } from './packSystem';
import {
  hasPlannedSportCoordination,
  resolveClientCarePathway,
} from './clientCarePathway';

const REQUIRED_SPORT_FIELDS = Object.freeze([
  ['typeSport', 'Activité sportive actuelle'],
  ['frequenceSport', 'Fréquence actuelle'],
  ['objectifSport', 'Objectif sportif'],
]);

function text(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function calculateAge(form = {}, now = new Date()) {
  const birthDate = text(form.dateNaissance || form.date_naissance);
  if (birthDate) {
    const birth = new Date(`${birthDate}T00:00:00`);
    if (!Number.isNaN(birth.getTime()) && birth <= now) {
      let age = now.getFullYear() - birth.getFullYear();
      const monthDelta = now.getMonth() - birth.getMonth();
      if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < birth.getDate())) age -= 1;
      if (age >= 0 && age <= 120) return age;
    }
  }
  const fallback = Number(form.age);
  return Number.isInteger(fallback) && fallback >= 0 && fallback <= 120 ? fallback : null;
}

function row(label, value) {
  return { label, value: text(value) };
}

function nonEmptyRows(rows) {
  return rows.filter((entry) => entry.value !== null);
}

export function buildSportHandoffReport(client, now = new Date()) {
  const form = client?.form || {};
  const firstName = text(client?.prenom || form.prenom) || 'Cliente';
  const lastName = text(client?.nom || form.nom);
  const packType = client?.packType || client?.pack_type || null;
  const pack = PACK_DEFINITIONS[packType] || null;
  const carePathway = resolveClientCarePathway(form);
  const coordinationPlanned = hasPlannedSportCoordination(form);
  const missing = REQUIRED_SPORT_FIELDS
    .filter(([key]) => !text(form[key]))
    .map(([, label]) => label);

  return {
    version: 1,
    generatedAt: now.toISOString(),
    clientName: [firstName, lastName].filter(Boolean).join(' '),
    age: calculateAge(form, now),
    contextLabel: carePathway.label,
    packLabel: pack?.label || 'Pack à vérifier',
    coordinationPlanned,
    contextNotice: coordinationPlanned
      ? null
      : carePathway.key
        ? 'Ce dossier n’est pas marqué comme suivi sport. Ce relais ponctuel ne change pas son contexte.'
        : 'Le contexte du dossier n’est pas encore renseigné. Ce relais reste ponctuel et ne le modifie pas.',
    ready: missing.length === 0,
    missing,
    sections: [
      {
        key: 'sport',
        title: 'Pratique et objectif déclarés',
        rows: nonEmptyRows([
          row('Activité actuelle', form.typeSport),
          row('Fréquence actuelle', form.frequenceSport),
          row('Objectif sportif', form.objectifSport),
        ]),
      },
      {
        key: 'recovery',
        title: 'Récupération et quotidien déclarés',
        rows: nonEmptyRows([
          row('Récupération', form.recuperation),
          row('Énergie au quotidien', form.energieJournee),
          row('Sommeil', form.heuresSommeil ? `${form.heuresSommeil} h déclarées` : null),
          row('Hydratation déclarée', form.hydratation),
        ]),
      },
      {
        key: 'attention',
        title: 'Éléments déclarés à vérifier avant les séances',
        rows: nonEmptyRows([
          row('Blessures', form.blessures),
          row('Douleurs actuelles', form.douleursActuelles),
          row('Confort digestif à l’effort', form.digestifEffort),
        ]),
      },
    ],
  };
}

function slug(value) {
  return String(value || 'cliente')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase() || 'cliente';
}

function reportRow(entry) {
  return new Paragraph({
    spacing: { after: 120, line: 300 },
    children: [
      new TextRun({ text: `${entry.label} · `, bold: true, color: '1A2E1F' }),
      new TextRun({ text: entry.value, color: '333330' }),
    ],
  });
}

export async function exportSportHandoffToWord(report, options = {}) {
  if (!report?.ready) throw new Error('Le dossier sport doit être complété avant l’export.');
  if (options.confirmed !== true) throw new Error('La relecture d’Anissa doit être confirmée avant l’export.');

  const note = text(options.anissaNote);
  const date = new Date(report.generatedAt).toLocaleDateString('fr-CH', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
  const children = [
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 120 },
      children: [new TextRun({ text: 'ANISSA DEROUBAIX NUTRITION', bold: true, color: '1A2E1F', characterSpacing: 45 })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 360 },
      children: [new TextRun({ text: 'Coordination sport · Document confidentiel', italics: true, color: '8A8A7A' })],
    }),
    new Paragraph({
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.CENTER,
      spacing: { after: 120 },
      children: [new TextRun({ text: 'Relais sport', color: '1A2E1F' })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 360 },
      children: [new TextRun({ text: `${report.clientName} · ${date}`, color: '555550' })],
    }),
    new Paragraph({
      spacing: { after: 120 },
      children: [new TextRun({ text: `Contexte · ${report.contextLabel}`, bold: true })],
    }),
    new Paragraph({
      spacing: { after: 320 },
      children: [new TextRun({ text: `Pack · ${report.packLabel}${Number.isInteger(report.age) ? ` · ${report.age} ans` : ''}` })],
    }),
  ];

  report.sections.forEach((section) => {
    children.push(new Paragraph({
      heading: HeadingLevel.HEADING_2,
      spacing: { before: 240, after: 140 },
      children: [new TextRun({ text: section.title, color: '1A2E1F' })],
    }));
    if (section.rows.length === 0) {
      children.push(new Paragraph({
        spacing: { after: 120 },
        children: [new TextRun({ text: 'Non renseigné dans le dossier.', italics: true, color: '8A8A7A' })],
      }));
    } else {
      children.push(...section.rows.map(reportRow));
    }
  });

  if (note) {
    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 300, after: 140 },
        children: [new TextRun({ text: 'Note de relais validée par Anissa', color: '1A2E1F' })],
      }),
      new Paragraph({ spacing: { after: 240, line: 320 }, children: [new TextRun({ text: note })] }),
    );
  }

  children.push(new Paragraph({
    spacing: { before: 420, after: 0 },
    border: { top: { color: 'C4A050', size: 6, space: 8, style: BorderStyle.SINGLE } },
    children: [new TextRun({
      text: 'Rapport factuel relu par Anissa. Il ne constitue ni un certificat médical, ni une décision d’aptitude sportive. Tout doute médical reste à coordonner avec le médecin de la cliente.',
      italics: true,
      color: '6B665C',
      size: 18,
    })],
  }));

  const footer = new Footer({
    children: [new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({ text: 'Relais sport confidentiel · ', color: '8A8A7A', size: 16 }),
        new TextRun({ children: [PageNumber.CURRENT], color: '8A8A7A', size: 16 }),
        new TextRun({ text: ' / ', color: '8A8A7A', size: 16 }),
        new TextRun({ children: [PageNumber.TOTAL_PAGES], color: '8A8A7A', size: 16 }),
      ],
    })],
  });

  const document = new Document({
    creator: 'Anissa Deroubaix Nutrition',
    title: `Relais sport — ${report.clientName}`,
    description: 'Rapport factuel de coordination sport relu par Anissa',
    sections: [{
      properties: { page: { margin: { top: 1080, right: 1080, bottom: 1080, left: 1080 } } },
      footers: { default: footer },
      children,
    }],
  });

  const blob = await Packer.toBlob(document);
  const filename = `Relais-sport-${slug(report.clientName)}.docx`;
  saveAs(blob, filename);
  return { ok: true, filename };
}
